import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createUserProxy,
  getUserProxies,
  revokeUserProxy,
  type UserProxyQuery,
} from '../api/userProxyApi'
import type { PagedResponse, UserProxy, UserProxyCreate } from '../types'

const KEY = 'admin-user-proxies'

/**
 * Admin → Vekalet sunucu-durumu (090): filtreli/sayfalı liste + iki mutation (ekle · kaldır).
 *
 * `i18n.language` queryKey'in parçası **değildir**: satırlarda sunucuda dile göre çözülen hiçbir alan
 * yoktur (ad/soyad/sicil ve tarihler dilden bağımsızdır; durum etiketi istemcide üretilir). 078'in
 * aksine dil segmenti eklemek yalnız gereksiz iki katı önbellek tutardı.
 *
 * `keepPreviousData`: sunucu-taraflı sayfalamada sayfa değişirken `isLoading` yeniden `true` olur;
 * paylaşılan `DataTable`'ın iskelet dalı paginator'ı render etmediği için sayfalama kontrolü kaybolup
 * tablo titrer. Önceki veri korunarak bu önlenir (repodaki diğer sunucu-sayfalı listelerle aynı kalıp).
 */
export function useUserProxies(query: UserProxyQuery) {
  const qc = useQueryClient()

  const list = useQuery({
    queryKey: [
      KEY,
      query.fromUserId ?? null,
      query.toUserId ?? null,
      query.recordStatus ?? null,
      query.page,
      query.size,
    ],
    queryFn: () => getUserProxies(query),
    placeholderData: keepPreviousData,
  })

  /**
   * Güncellenmiş satırı önbellekteki **tüm** liste sayfalarına hemen yazar (078
   * `useSentEmails.applyUpdated` deseni). Anahtar prefix'le taranır (`setQueriesData`): liste anahtarı
   * üç filtre + sayfa + boyut taşır, çağıranın o bileşimi yeniden kurması kırılgan olurdu.
   */
  const applyUpdated = (updated: UserProxy) =>
    qc.setQueriesData<PagedResponse<UserProxy>>({ queryKey: [KEY] }, (prev) =>
      prev && Array.isArray(prev.content)
        ? { ...prev, content: prev.content.map((r) => (r.id === updated.id ? updated : r)) }
        : prev,
    )

  const create = useMutation({
    mutationFn: (body: UserProxyCreate) => createUserProxy(body),
    // Yeni kayıt listenin BAŞINA düşer (GRANT_DATE DESC) ve sayfa dolabilir → satır yerleştirmek
    // yerine tazeleme yapılır. Promise DÖNDÜRÜLMEZ (aşağıdaki gerekçe).
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] })
    },
  })

  /**
   * Vekaleti kaldır → `RECORD_STATUS = 0` + `REVOKE_DATE`. Değişken olarak `{ id, modDate }` alır;
   * damga listeden gelen satırın `modDate`'idir (uç optimistic lock uygular).
   *
   * **`invalidateQueries` promise'i bilinçli olarak DÖNDÜRÜLMEZ** (gövde `{}` içinde): React Query
   * `onSuccess`'ten dönen promise'i bekler ve mutation ancak o zaman "settled" olur. Beklenirse bu
   * anahtar altındaki tüm liste sorguları yeniden çekilene kadar mutation açık kalır; biri yavaşlarsa
   * `onSettled` çalışmaz → satırın kilidi açılmaz ve buton kalıcı olarak disabled kalır (059/078'de
   * belgelenen tuzak). Satır zaten `applyUpdated` ile güncellendiğinden tazeleme arka planda sürebilir.
   *
   * Not: aktif filtre "Aktif" ise kaldırılan satır tazelemeden sonra listeden **düşer** — bu doğru
   * davranıştır (kayıt artık Pasif'tir); `applyUpdated` yalnız o ana kadar tutarlı bir görüntü verir.
   */
  const revoke = useMutation({
    // Optimistic lock damgası ZORUNLU (uç 400/409 verir) → satırın `modDate`'i birlikte taşınır.
    mutationFn: ({ id, modDate }: { id: number; modDate: string }) => revokeUserProxy(id, modDate),
    onSuccess: (data) => {
      applyUpdated(data)
      qc.invalidateQueries({ queryKey: [KEY] })
    },
  })

  return { list, create, revoke }
}
